/**
 * Helpers for rendering analytics page paths in tables/search.
 * Nested routes (/blog/..., /templates/...) get a section chip + leaf label
 * so long CMS/template URLs stay scannable.
 */

type ParsedPagePath = {
  full: string
  /** First path segment for nested routes, e.g. "blog" */
  section: string | null
  /** Last path segment (or "/" for home) */
  leaf: string
  crumbs: string[]
  isHome: boolean
  isNested: boolean
}

const SECTION_LABELS: Record<string, string> = {
  blog: "Blog",
  blogs: "Blog",
  articles: "Articles",
  article: "Article",
  news: "News",
  posts: "Posts",
  post: "Post",
  docs: "Docs",
  documentation: "Docs",
  help: "Help",
  support: "Support",
  templates: "Templates",
  template: "Template",
  resources: "Resources",
  resource: "Resource",
  guides: "Guides",
  guide: "Guide",
  products: "Products",
  product: "Product",
  shop: "Shop",
  store: "Store",
  collections: "Collections",
  collection: "Collection",
  categories: "Categories",
  category: "Category",
  pricing: "Pricing",
  features: "Features",
}

export function parsePagePath(page: string | null | undefined): ParsedPagePath {
  const full = page && page.length > 0 ? page : "/"
  if (full === "/") {
    return {
      full: "/",
      section: null,
      leaf: "/",
      crumbs: [],
      isHome: true,
      isNested: false,
    }
  }

  const crumbs = full.split("/").filter(Boolean)
  const leaf = crumbs[crumbs.length - 1] ?? full
  const isNested = crumbs.length > 1
  const section = isNested ? crumbs[0] : null

  return { full, section, leaf, crumbs, isHome: false, isNested }
}

/** Turn a URL slug into a short readable label. */
export function humanizePathSegment(segment: string): string {
  try {
    return decodeURIComponent(segment)
      .replace(/[-_]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
  } catch {
    return segment.replace(/[-_]+/g, " ").trim()
  }
}

/** Title-case a known section, otherwise return the raw segment. */
export function formatPathSection(section: string | null | undefined): string | null {
  if (!section) return null
  const key = section.toLowerCase()
  if (SECTION_LABELS[key]) return SECTION_LABELS[key]
  return section
}

/** Client-side search match used as a safety net alongside server `?search=`. */
export function matchesPageSearch(
  page: string | null | undefined,
  search: string,
  extra: Array<string | null | undefined> = [],
): boolean {
  const q = search.trim().toLowerCase()
  if (!q) return true
  const haystacks = [page ?? "", ...extra.filter(Boolean) as string[]]
  return haystacks.some((value) => value.toLowerCase().includes(q))
}
